import { SelectField } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { FormState, Option, SarFormValues, Translate } from './compliance-types'
import { FormAlert, TextInput } from './form-parts'

/**
 * Props for {@link SubjectAccessFormView}. Rendered only for a viewer holding
 * `compliance.sar.manage`.
 *
 * @property candidates - The customer ids of a `SUBJECT_AMBIGUOUS` refusal; empty otherwise.
 * @property onChooseCandidate - Resubmits the request for one candidate by `CUSTOMER_ID`.
 */
export interface SubjectAccessFormViewProps extends FormState<SarFormValues> {
	t: Translate
	identifierTypes: Option[]
	candidates: string[]
	onChooseCandidate: (customerId: string) => void
}

/** The "New access request" form, with the choice between several matching customers (organism). */
export function SubjectAccessFormView(props: SubjectAccessFormViewProps): ReactElement {
	const { t, values, errors, onChange } = props
	const title = t('sars.form.title')
	return (
		<form
			aria-label={title}
			className="flex flex-col gap-3"
			onSubmit={(event) => {
				event.preventDefault()
				props.onSubmit()
			}}
		>
			<h2 className="text-ds-lg font-semibold text-ds-text">{title}</h2>
			<SelectField
				label={t('sars.form.identifierType')}
				value={values.identifierType}
				error={errors.identifierType}
				onValueChange={(v) => onChange('identifierType', v)}
				options={props.identifierTypes.map((o) => ({ id: o.id, text: o.label }))}
			/>
			<TextInput
				label={t('sars.form.identifierValue')}
				value={values.identifierValue}
				error={errors.identifierValue}
				onChange={(v) => onChange('identifierValue', v)}
			/>
			<FormAlert message={props.message} />
			{props.candidates.length > 0 ? (
				<ul className="flex flex-col gap-2">
					{props.candidates.map((customerId) => (
						<li key={customerId} className="flex items-center gap-2 text-ds-sm text-ds-text">
							<span className="break-all">{customerId}</span>
							<Button variant="muted" size="sm" onPress={() => props.onChooseCandidate(customerId)}>
								{t('sars.form.useCustomer')}
							</Button>
						</li>
					))}
				</ul>
			) : null}
			<Button type="submit" isDisabled={props.submitting}>
				{t('sars.form.submit')}
			</Button>
		</form>
	)
}
